import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Repeat, ArrowUpCircle, Layers, Briefcase, Store, Users, UserX, Building2, Sprout, Landmark } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ScriptCallout, ChoiceGrid, ChoiceCard, SectionHeading, CibilBadge } from './CockpitUI';

const reveal = { initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3, ease: 'easeOut' } };

const PURPOSE_ICONS = { PURCHASE: Target, BALANCE_TRANSFER: Repeat, TOP_UP: ArrowUpCircle, BT_PLUS_TOPUP: Layers };

const EARNING_ICONS = {
  SALARIED: Briefcase, BUSINESS: Store, PROFESSIONAL: Building2,
  AGRICULTURE: Sprout, PENSION: Landmark, NO_INCOME: UserX,
};

// Used only until /meta answers — the server is the source of truth.
const FALLBACK_EARNING_PROFILES = [
  { value: 'SALARIED', label: 'Salaried', subtitle: 'Salary slips + bank statement', employment_type: 'SALARIED', category: 'Salaried' },
  { value: 'BUSINESS', label: 'Business / trade', subtitle: 'ITR + business banking', employment_type: 'SELF_EMPLOYED', category: 'Business' },
  { value: 'PROFESSIONAL', label: 'Professional practice', subtitle: 'Doctor, CA, lawyer — ITR + receipts', employment_type: 'SELF_EMPLOYED', category: 'Self-Employed Professional' },
  { value: 'AGRICULTURE', label: 'Agriculture', subtitle: 'Land records, no salary slip', employment_type: 'AGRICULTURE', category: 'Agriculturist' },
  { value: 'PENSION', label: 'Pension', subtitle: 'Pension order + bank credit', employment_type: 'PENSION', category: 'Pensioner' },
  { value: 'NO_INCOME', label: 'No income on paper', subtitle: 'Carried by property, a guarantor or a co-applicant', employment_type: null, category: null },
];

export const PurposeStep = ({ meta, value, onSelect }) => (
  <div>
    <SectionHeading hint="This decides the documents and disbursal path.">Loan Type</SectionHeading>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {(meta?.loan_purposes || []).map((p) => (
        <ChoiceCard key={p.value} title={p.label} subtitle={p.subtitle} compact
          icon={PURPOSE_ICONS[p.value] || Target}
          selected={value === p.value} onClick={() => onSelect(p.value)}
          testId={`loan-type-card-${p.value}`} />
      ))}
    </div>
  </div>
);

export const CibilStep = ({ creditScore, bureau, onSave }) => {
  const [val, setVal] = useState(creditScore || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = parseInt(val, 10);
    if (!n || n < 300 || n > 900) return;
    setSaving(true);
    try { await onSave(n); } finally { setSaving(false); }
  };
  return (
    <div>
      <ScriptCallout testId="script-callout-cibil">
        "Let me quickly check the credit score. May I confirm a few KYC details to pull the CIBIL?"
      </ScriptCallout>
      <SectionHeading hint="Pull/record the credit score — it gates eligibility and rate band.">CIBIL Score</SectionHeading>
      <div className="flex items-center gap-3 mb-5">
        <span className="text-sm text-slate-600">Current:</span>
        <CibilBadge score={creditScore} testId="cibil-current-badge" />
        {bureau && <span className="text-xs text-slate-400 uppercase">{bureau}</span>}
      </div>
      <div className="flex items-end gap-3 max-w-md">
        <div className="flex-1">
          <Label className="text-sm">Record CIBIL score (300–900)</Label>
          <Input type="number" value={val} onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 742" className="mt-1" data-testid="cibil-score-input" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="cibil-save-btn">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Tip: use the bureau OTP flow on the lead to auto-fetch, or record the value you already pulled.
      </p>
    </div>
  );
};

/** How the customer earns — asked once, here, because the answer decides both the
    income questions below and the documents the file needs. Before this, the desk
    asked income/no-income here and the category again on Personal details, and a
    no-income file still showed salary fields. */
export const IncomeProfileBlock = ({ profile, onPatch, earningProfiles, personal }) => {
  const cp = profile || {};
  const options = earningProfiles?.length ? earningProfiles : FALLBACK_EARNING_PROFILES;
  const chosen = cp.earning_profile
    || (cp.income_type === 'NIP' ? 'NO_INCOME'
      : options.find((o) => o.employment_type && o.employment_type === cp.employment_type
        && (!cp.income_category || o.category === cp.income_category))?.value)
    || '';
  const noIncome = chosen === 'NO_INCOME';
  const ownsHouse = (personal?.own_house_available || '') === 'Yes'
    || cp.property_owns_house === true;
  const nip = cp.nip_basis;

  const set = (patch) => onPatch(patch);

  return (
    <div className="space-y-8" data-testid="income-profile-block">
      <div>
        <ScriptCallout testId="script-callout-income">
          "How do you earn — a salary, your own business, farming, or a pension?"
        </ScriptCallout>
        <SectionHeading hint="One answer sets the income questions below and the document list.">
          How the customer earns
        </SectionHeading>
        <ChoiceGrid cols={3}>
          {options.map((o) => (
            <ChoiceCard key={o.value} title={o.label} subtitle={o.subtitle} compact
              icon={EARNING_ICONS[o.value] || Briefcase}
              selected={chosen === o.value}
              onClick={() => set({ earning_profile: o.value })}
              testId={`choice-btn-earning-${o.value}`} />
          ))}
        </ChoiceGrid>
      </div>

      {/* No income — the file has to be carried by something else */}
      {noIncome && (
        <motion.div {...reveal} className="space-y-6">
          <div className={`rounded-lg px-3.5 py-2.5 text-sm ${ownsHouse ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}
            data-testid="nip-residence-readout">
            {ownsHouse
              ? 'Owns a house — the property carries this file. Residence and the EB bill are on the Personal details tab.'
              : 'No own house on the Personal details tab, so this file needs a guarantor or a co-applicant.'}
          </div>

          {!ownsHouse && (
            <div>
              <ScriptCallout testId="script-callout-guarantor">
                "Is there a guarantor who can support the loan — ideally a government employee?"
              </ScriptCallout>
              <SectionHeading>Guarantor</SectionHeading>
              <ChoiceGrid cols={3}>
                <ChoiceCard title="Govt employee" subtitle="Strongest" icon={Users}
                  selected={nip === 'GUARANTOR_GOVT'} onClick={() => set({ nip_basis: 'GUARANTOR_GOVT' })} testId="choice-btn-guarantor-govt" />
                <ChoiceCard title="Private employee" subtitle="Acceptable" icon={Users}
                  selected={nip === 'GUARANTOR_PRIVATE'} onClick={() => set({ nip_basis: 'GUARANTOR_PRIVATE' })} testId="choice-btn-guarantor-private" />
                <ChoiceCard title="No guarantor" subtitle="Needs co-applicant" icon={UserX}
                  selected={nip === 'NONE'} onClick={() => set({ nip_basis: 'NONE' })} testId="choice-btn-guarantor-none" />
              </ChoiceGrid>
              {(nip === 'GUARANTOR_GOVT' || nip === 'GUARANTOR_PRIVATE') && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div><Label className="text-sm">Guarantor name</Label>
                    <Input value={cp.guarantor_name || ''} onChange={(e) => set({ guarantor_name: e.target.value })} className="mt-1" data-testid="guarantor-name-input" /></div>
                  <div><Label className="text-sm">Relationship</Label>
                    <Input value={cp.guarantor_relationship || ''} onChange={(e) => set({ guarantor_relationship: e.target.value })} className="mt-1" data-testid="guarantor-rel-input" /></div>
                  <div><Label className="text-sm">Employer</Label>
                    <Input value={cp.guarantor_employer || ''} onChange={(e) => set({ guarantor_employer: e.target.value })} className="mt-1" data-testid="guarantor-employer-input" /></div>
                </div>
              )}
            </div>
          )}

          {ownsHouse && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Property city</Label>
                <Input value={cp.property_city || ''} onChange={(e) => set({ property_city: e.target.value })}
                  placeholder="e.g. Mysuru" className="mt-1" data-testid="property-city-input" />
              </div>
              <ChoiceCard title="House is in the hometown" subtitle="Banks weigh a hometown property higher"
                selected={cp.property_in_hometown === true}
                onClick={() => set({ property_in_hometown: !cp.property_in_hometown })} testId="choice-btn-hometown" />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export const ExistingLoanStep = ({ existingLoan, onSave }) => {
  const [f, setF] = useState(existingLoan || {});
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => { setSaving(true); try { await onSave(f); } finally { setSaving(false); } };
  return (
    <div>
      <ScriptCallout testId="script-callout-existing-loan">
        "Tell me about your current loan — which lender, the EMI, and how many EMIs you've already paid?"
      </ScriptCallout>
      <SectionHeading hint="Balance Transfer & Top-up depend on a clean repayment track (11–12 EMIs).">Existing loan</SectionHeading>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><Label className="text-sm">Current lender</Label><Input value={f.lender_name || ''} onChange={(e) => upd('lender_name', e.target.value)} className="mt-1" data-testid="existing-lender-input" /></div>
        <div><Label className="text-sm">Outstanding (₹)</Label><Input type="number" value={f.outstanding_amount || ''} onChange={(e) => upd('outstanding_amount', parseFloat(e.target.value) || null)} className="mt-1" data-testid="existing-outstanding-input" /></div>
        <div><Label className="text-sm">EMI (₹)</Label><Input type="number" value={f.emi_amount || ''} onChange={(e) => upd('emi_amount', parseFloat(e.target.value) || null)} className="mt-1" data-testid="existing-emi-input" /></div>
        <div><Label className="text-sm">Interest rate (%)</Label><Input type="number" value={f.interest_rate || ''} onChange={(e) => upd('interest_rate', parseFloat(e.target.value) || null)} className="mt-1" data-testid="existing-rate-input" /></div>
        <div><Label className="text-sm">EMIs paid</Label><Input type="number" value={f.emis_paid || ''} onChange={(e) => upd('emis_paid', parseInt(e.target.value, 10) || null)} className="mt-1" data-testid="existing-emis-paid-input" /></div>
        <div><Label className="text-sm">Foreclosure amount (₹)</Label><Input type="number" value={f.foreclosure_amount || ''} onChange={(e) => upd('foreclosure_amount', parseFloat(e.target.value) || null)} className="mt-1" data-testid="existing-foreclosure-input" /></div>
      </div>
      <div className="flex items-center gap-6 mt-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!f.repayment_clean} onChange={(e) => upd('repayment_clean', e.target.checked)} data-testid="existing-clean-checkbox" />
          Clean repayment track
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!f.is_npa} onChange={(e) => upd('is_npa', e.target.checked)} data-testid="existing-npa-checkbox" />
          Currently overdue / NPA
        </label>
      </div>
      <Button className="mt-5" onClick={save} disabled={saving} data-testid="existing-loan-save-btn">
        {saving ? 'Saving…' : 'Save existing loan'}
      </Button>
    </div>
  );
};
